import { useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { Dialog } from "../../../components/ui";
import { Icon } from "../../../components/Icon";
import { today as todayIn } from "../../../lib/format";
import { useViewerZone } from "../../../lib/zeitzone";
import { FadenZeile, ThreadPanel, useChatSeitenHoehe } from "./chat";
import { Rundnachricht } from "./rundnachricht";
import { useAgencyThreads, useMembers } from "./hooks";
import { countLabel, shortWhen, threadLines, visibleThreadLines, type MessageRow, type ThreadFilter } from "./model";
import "./nachrichten.css";
import { t } from "../../../lib/sprache";
import { NurSchreiben } from "../../../app/nurLesen";

/**
 * Nachrichten der Agentur (11_app_agentur/chat.html) mit dem Umfang des Altportals (AD-180 … AD-186): alle Unterhaltungen
 * mit Suche und Ungelesen-Zahl, neue Unterhaltung über die Suche, Verlauf, Senden, Dateien, Verknüpfungen, Reaktionen.
 * Weg: /agentur/nachrichten und /agentur/nachrichten/<creator>. Wer welche Creator sieht, entscheidet die Datenbank.
 */

const FILTERS: { key: ThreadFilter; label: string }[] = [
  { key: "alle", get label() { return t("Alle", "All"); } },
  { key: "ungelesen", get label() { return t("Ungelesen", "Unread"); } },
];

export function AgenturNachrichten() {
  const ws = useStaffWorkspace();
  const talentId = useParams().talentId ?? null;
  // K-516: ältere Links /agentur/nachrichten?talent=<id> öffnen den Verlauf; ?objekt=kind:id belegt das Eingabefeld vor (K-517)
  const [params] = useSearchParams();
  const alterLink = !talentId ? params.get("talent") : null;
  const vorbelegt = params.get("objekt");
  // K-525: aus der Glocke (Erwähnung in einer internen Notiz) direkt zur Notiz
  const notiz = params.get("notiz");
  const orgId = ws.org.org_id;
  // Zeiten in der Zone der betrachtenden Person, wie Kalender und Glocke (Befund 12)
  const timeZone = useViewerZone(ws.org.timezone);
  const today = todayIn(timeZone);
  const base = useAgencyThreads(orgId);
  const members = useMembers(orgId);
  const qc = useQueryClient();
  // Nach der Rundnachricht Liste, offenen Verlauf und Zähler neu laden (sonst laden sie mit Supabase alle 30 s nach)
  const refresh = () => void qc.invalidateQueries({ predicate: (q) => ["my_chat_threads", "messages", "unread"].includes(String(q.queryKey[0])) });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ThreadFilter>("alle");
  const [rund, setRund] = useState(false);
  useChatSeitenHoehe();
  const lines = useMemo(() => (base.data ? threadLines(base.data.threads, base.data.talents, "agency") : []), [base.data]);
  const memberName = useMemo(() => new Map((members.data ?? []).map((m) => [m.user_id, m.display_name])), [members.data]);

  if (alterLink && /^[0-9a-f-]{36}$/.test(alterLink)) return <Navigate to={`/agentur/nachrichten/${alterLink}${vorbelegt ? `?objekt=${encodeURIComponent(vorbelegt)}` : ""}`} replace />;
  if (base.error) return <ErrorState error={base.error} retry={() => void base.refetch()} />;
  if (base.isPending) return <Loading />;

  const me = api.session()?.userId ?? null;
  const shown = visibleThreadLines(lines, search, filter, talentId);
  const selected = talentId ? lines.find((l) => l.talentId === talentId) ?? null : null;
  const unread = lines.reduce((s, l) => s + l.unread, 0);
  const unreadThreads = lines.filter((l) => l.unread > 0).length;
  const withMessages = lines.filter((l) => l.lastAt).length;
  const restricted = !ws.isChef && !ws.membership.sees_all_talents;
  // Absender nennen, wenn eine andere Person aus dem Team geschrieben hat (eigene und Creator-Nachrichten sind eindeutig)
  const senderName = (m: MessageRow) =>
    m.sender_side === "agency" && m.sender_user_id && m.sender_user_id !== me ? memberName.get(m.sender_user_id) ?? "Team" : null;

  const summary = [
    countLabel(withMessages, t("Unterhaltung", "conversation"), t("Unterhaltungen", "conversations")),
    unread ? `${countLabel(unread, t("ungelesene Nachricht", "unread message"), t("ungelesene Nachrichten", "unread messages"))}${unreadThreads > 1 ? t(` von ${unreadThreads} Creator`, ` from ${unreadThreads} creators`) : ""}` : t("alles gelesen", "all read"),
  ].join(" · ");

  return (
    <main id="main" className={`content tk-chat-seite${talentId ? " tk-chat-open" : ""}`}>
      {/* D-1 S6: Seitenkopf als eine schmale Zeile; darunter Liste (300 px) und Verlauf in voller Höhe, nur der Verlauf scrollt */}
      <div className="tk-chat-seitenkopf">
        <h1>{t("Nachrichten", "Messages")}</h1>
        <p>{summary}{restricted ? t(" · nur dir zugewiesene Creator", " · only creators assigned to you") : ""}</p>
        {api.mode === "demo" && <small className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</small>}
        <button type="button" className="tk-chat-rund-knopf" onClick={() => setRund(true)} disabled={!base.data.talents.some((t) => t.status === "active")}>{t("Rundnachricht", "Broadcast")}</button>
      </div>
      <Dialog open={rund} onClose={() => setRund(false)} title={t("Rundnachricht an mehrere Creator", "Broadcast to several creators")} wide>
        {rund && <Rundnachricht orgId={orgId} seesAll={ws.isChef || ws.membership.sees_all_talents} talents={base.data.talents} onDone={() => { setRund(false); refresh(); }} />}
      </Dialog>

      <div className="tk-chat-rahmen">
        <section className="tk-chat-list" data-coco="chat-liste" aria-labelledby="unterhaltungen">
          <h3 id="unterhaltungen" className="sr-only">{t("Unterhaltungen", "Conversations")}</h3>
          <div className="tk-chat-list-kopf">
            <label className="tk-chat-list-suche">
              <span className="sr-only">{t("Creator suchen", "Search creators")}</span>
              <Icon name="search" />
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Creator suchen …", "Search creators …")} />
            </label>
            {/* Alle / Ungelesen als kleine Reiter; ohne „Aktualisieren“: Liste und Verlauf laden mit Supabase alle 30 s nach (hooks.ts) */}
            <div className="tk-chat-filter" role="group" aria-label={t("Filter", "Filter")}>
              {FILTERS.map((f) => (
                <button key={f.key} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                  {f.label}{f.key === "ungelesen" && unreadThreads > 0 ? <span className="tk-chat-filter-zahl">{unreadThreads}</span> : null}
                </button>
              ))}
            </div>
          </div>
          <div className="tk-chat-list-zeilen">
            {shown.map((l) => (
              <FadenZeile key={l.talentId} name={l.name} to={`/agentur/nachrichten/${l.talentId}`} current={l.talentId === talentId} unread={l.unread}
                when={l.lastAt ? shortWhen(l.lastAt, today, timeZone) : null}
                preview={`${l.lastAt ? l.preview : t("Noch keine Nachrichten · Unterhaltung beginnen", "No messages yet · start a conversation")}${l.archived ? t(" · Archiviert", " · Archived") : ""}`} />
            ))}
            {shown.length === 0 && (
              lines.length === 0 ? (
                <div className="tk-chat-list-leer">
                  <p><strong>{restricted ? t("Dir sind noch keine Creator zugewiesen.", "No creators have been assigned to you yet.") : t("Noch keine Creator.", "No creators yet.")}</strong></p>
                  <p>{restricted ? t("Wende dich an deinen Admin.", "Contact your admin.") : t("Lege zuerst einen Creator an, dann kannst du ihm hier schreiben.", "Create a creator first, then you can write to them here.")}</p>
                  {!restricted && <NurSchreiben><Link className="btn" to="/agentur/creator/einladen">{t("Creator einladen", "Invite creator")}</Link></NurSchreiben>}
                </div>
              ) : search.trim() || filter !== "alle" ? (
                <div className="tk-chat-list-leer">
                  <p><strong>{t("Keine passenden Ergebnisse.", "No matching results.")}</strong></p>
                  <p>{t("Passe die Suche oder die Filter an.", "Adjust the search or the filters.")}</p>
                  <div className="row"><button type="button" onClick={() => { setSearch(""); setFilter("alle"); }}>{t("Filter zurücksetzen", "Reset filters")}</button></div>
                </div>
              ) : (
                <div className="tk-chat-list-leer">
                  <p><strong>{t("Noch keine Unterhaltungen.", "No conversations yet.")}</strong></p>
                  <p>{t("Suche oben nach einem Creator, um die erste Nachricht zu schreiben.", "Search for a creator above to write the first message.")}</p>
                </div>
              )
            )}
          </div>
        </section>

        {selected ? (
          <ThreadPanel
            key={selected.talentId}
            portal="agentur"
            orgId={orgId}
            talentId={selected.talentId}
            title={selected.name}
            titleHref={`/agentur/creator/${selected.talentId}`}
            timeZone={timeZone}
            talentName={selected.name}
            vorbelegt={vorbelegt}
            senderName={senderName}
            memberName={(id) => (id ? memberName.get(id) ?? null : null)}
            focusNote={notiz}
            back="/agentur/nachrichten"
            notice={selected.archived
              ? <>{t("Archiviert: ", "Archived: ")}{selected.name} {t("kann das Portal nicht mehr öffnen und sieht neue Nachrichten erst, wenn der Creator wieder aktiv ist.", "can no longer open the portal and will only see new messages once the creator is active again.")}</>
              : undefined}
          />
        ) : talentId ? (
          <section className="tk-faden tk-faden-leer" aria-labelledby="unterhaltung" role="alert">
            <Link className="btn tk-back" to="/agentur/nachrichten">{t("Zu allen Unterhaltungen", "All conversations")}</Link>
            <h3 id="unterhaltung">{t("Unterhaltung", "Conversation")}</h3>
            <p><strong>{t("Diese Unterhaltung ist nicht freigegeben.", "This conversation is not available to you.")}</strong></p>
            <p>{restricted ? t("Du siehst nur Creator, die dir zugewiesen sind. Wende dich an deinen Admin.", "You only see creators assigned to you. Contact your admin.") : t("Diesen Creator gibt es in dieser Agentur nicht.", "This creator does not exist in this agency.")}</p>
          </section>
        ) : (
          <section className="tk-faden tk-faden-leer" aria-labelledby="unterhaltung">
            <span className="tk-faden-leer-zeichen" aria-hidden="true"><Icon name="chat" /></span>
            <h3 id="unterhaltung">{t("Unterhaltung", "Conversation")}</h3>
            <p>{t("Wähle eine Unterhaltung aus der Liste oder suche nach einem Creator, um eine neue zu beginnen.", "Choose a conversation from the list or search for a creator to start a new one.")}</p>
          </section>
        )}
      </div>
    </main>
  );
}
